import { z } from 'zod';
import { CONDITIONS, CURRENCIES, VIDEO_SYSTEMS } from '../constants/inventory.js';
// ---------------------------------------------------------------------------
// Custom vault definition schemas
// ---------------------------------------------------------------------------
/** Reserved keys that must not be used as custom field keys. */
const RESERVED_FIELD_KEYS = new Set([
    'name', 'category', 'platform', 'condition', 'quantity', 'description',
    'acquisitionDate', 'acquisitionPrice', 'currency', 'imageUrl', 'showcase',
    'listingStatus', 'askingPrice', 'listingCurrency', 'videoSystem',
    'subcategory', 'location', 'customFields', 'customNotes',
    'launchDate', 'launchPrice', 'launchPriceCurrency', 'launchPriceInflationAdjusted',
]);
const SLUG_REGEX = /^[a-z][a-z0-9_]*$/;
export const customFieldDefSchema = z.object({
    key: z
        .string()
        .min(1)
        .max(50)
        .regex(SLUG_REGEX, 'Key must be lowercase, start with a letter, and contain only letters, numbers, and underscores')
        .refine((k) => !RESERVED_FIELD_KEYS.has(k), { message: 'This field key is reserved' }),
    label: z.string().min(1, 'Label is required').max(100),
    type: z.enum(['text', 'number', 'select', 'date', 'boolean', 'url']),
    placeholder: z.string().max(200).optional(),
    options: z.array(z.string().min(1).max(100)).min(2).max(50).optional(),
}).refine((f) => f.type !== 'select' || (f.options && f.options.length >= 2), { message: 'Select fields must have at least 2 options' });
export const customVaultDefinitionSchema = z.object({
    displayName: z.string().min(1, 'Vault name is required').max(100),
    description: z.string().min(10, 'Please provide a detailed description (at least 10 characters)').max(2000),
    icon: z.string().min(1).max(50),
    platformLabel: z.string().min(1).max(50).default('Brand'),
    fields: z.array(customFieldDefSchema).min(1, 'At least one field is required').max(20),
}).refine((def) => {
    const keys = def.fields.map((f) => f.key);
    return keys.length === new Set(keys).size;
}, { message: 'Field keys must be unique' });
export const inventoryItemSchema = z.object({
    name: z.string().min(1, 'Name is required').max(255),
    category: z.string().min(1, 'Category is required').max(100),
    platform: z.string().min(1, 'Platform is required').max(100),
    condition: z.enum(CONDITIONS),
    quantity: z.number().int().min(0).default(1),
    description: z.string().max(5000).optional().default(''),
    acquisitionDate: z.string().nullable().optional(),
    acquisitionPrice: z
        .string()
        .regex(/^\d{1,8}(\.\d{1,2})?$/, 'Invalid price format')
        .nullable()
        .optional(),
    currency: z.enum(CURRENCIES).default('USD'),
    videoSystem: z.enum(VIDEO_SYSTEMS).nullable().optional(),
    subcategory: z.string().max(100).nullable().optional(),
    location: z.string().max(255).nullable().optional(),
    metadata: z.record(z.unknown()).optional().default({}),
});
export const createInventoryItemSchema = inventoryItemSchema;
export const updateInventoryItemSchema = inventoryItemSchema.partial();
export const inventoryQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    search: z.string().optional(),
    category: z.string().optional(),
    platform: z.string().optional(),
    condition: z.enum(CONDITIONS).optional(),
    videoSystem: z.enum(VIDEO_SYSTEMS).optional(),
    subcategory: z.string().optional(),
    vaultId: z.string().uuid().optional(),
    sortBy: z
        .enum([
        'name', 'category', 'platform', 'condition', 'subcategory',
        'createdAt', 'updatedAt', 'acquisitionDate', 'acquisitionPrice',
        'launchDate', 'launchPrice',
    ])
        .default('createdAt'),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
    showAll: z.preprocess((v) => v === 'true' || v === true, z.boolean()).default(false),
});
//# sourceMappingURL=inventory.js.map